import React, { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ReactComponent as Logo } from './logo.svg';

const Container = styled.div`
  min-height: 100vh;
  background: #FAFAFA;
  color: #2D2D2D;
  overflow-x: hidden;
  position: relative;
  font-family: -apple-system, BlinkMacSystemFont, 'Inter', sans-serif;
`;

const PatternBackground = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image: 
    radial-gradient(circle at 25% 25%, #B8B5D4 0%, transparent 50%),
    radial-gradient(circle at 75% 75%, #E4B5CB 0%, transparent 50%);
  opacity: 0.1;
  pointer-events: none;
`;

const FloatingOrb = styled(motion.div)`
  position: absolute;
  width: ${props => props.size || 300}px;
  height: ${props => props.size || 300}px;
  border-radius: 50%;
  background: ${props => props.color || '#FEC5BB'};
  filter: blur(20px);
  opacity: ${props => props.opacity || 0.4};
  pointer-events: none;
  will-change: transform;
  z-index: 1;
`;

const AnimatedBackground = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  overflow: hidden;
  pointer-events: none;
  z-index: 1;
`;

const NavBar = styled(motion.nav)`
  padding: 1rem 2rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 100;
  backdrop-filter: blur(20px);
  background: ${props => props.scrolled ? 'rgba(250, 250, 250, 0.9)' : 'transparent'};
  transition: all 0.3s ease;
  ${props => props.scrolled && 'box-shadow: 0 2px 20px rgba(0, 0, 0, 0.05);'}
  
  @media (min-width: 768px) {
    padding: 1rem 4rem;
  }
`;

const LogoWrapper = styled(motion.div)`
  width: 80px;
  height: 80px;
  cursor: pointer;
  
  @media (min-width: 768px) {
    width: 120px;
    height: 120px;
  }
  
  svg {
    width: 100%;
    height: 100%;
    filter: drop-shadow(0 2px 8px rgba(0, 0, 0, 0.1));
    
    path {
      fill: #2D2D2D;
    }
  }
`;

const NavLinks = styled.div`
  display: none;
  
  @media (min-width: 768px) {
    display: flex;
    gap: 3rem;
    font-weight: 500;
  }
  
  a {
    color: #2D2D2D;
    text-decoration: none;
    position: relative;
    transition: all 0.3s ease;
    font-size: 0.95rem;
    letter-spacing: 0.02em;
    opacity: 0.8;
    
    &:hover {
      opacity: 1;
      color: #FEC89A;
    }
    
    &::after {
      content: '';
      position: absolute;
      bottom: -8px;
      left: 0;
      width: 0;
      height: 2px;
      background: #B8B5D4;
      transition: width 0.3s ease;
    }
    
    &:hover::after {
      width: 100%;
    }
  }
`;

const MobileMenuButton = styled.button`
  display: flex;
  flex-direction: column;
  gap: 4px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 8px;
  
  @media (min-width: 768px) {
    display: none;
  }
  
  span {
    width: 24px;
    height: 2px;
    background: #2D2D2D;
    transition: all 0.3s ease;
  }
  
  ${props => props.open && `
    span:nth-of-type(1) {
      transform: rotate(45deg) translate(5px, 5px);
    }
    span:nth-of-type(2) {
      opacity: 0;
    }
    span:nth-of-type(3) {
      transform: rotate(-45deg) translate(5px, -5px);
    }
  `}
`;

const MobileMenu = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: #FAFAFA;
  padding: calc(80px + 2rem + 1rem) 2rem 2rem;
  display: flex;
  flex-direction: column;
  gap: 1.2rem;
  z-index: 99;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  box-sizing: border-box;
  
  a {
    color: #2D2D2D;
    text-decoration: none;
    font-size: 1.1rem;
    font-weight: 500;
    opacity: 0.8;
    padding: 0.75rem 1rem;
    border-radius: 8px;
    transition: all 0.2s ease;
    
    &:hover, &:active {
      opacity: 1;
      background: rgba(100, 166, 189, 0.1);
      color: #64A6BD;
    }
  }
`;

const Hero = styled.section`
  padding: 7rem 1.5rem 1rem;
  max-width: 1200px;
  margin: 0 auto;
  position: relative;
  z-index: 10;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  
  @media (min-width: 768px) {
    padding: 9rem 4rem 1rem;
  }
`;


const Title = styled(motion.h1)`
  font-size: clamp(2.5rem, 8vw, 5rem);
  font-weight: 800;
  margin: 0;
  line-height: 1.1;
  letter-spacing: -0.02em;
  color: #2D2D2D;
`;

const GradientText = styled.span`
  background: linear-gradient(135deg, #64A6BD 0%, #90A8C3 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const Subtitle = styled(motion.p)`
  font-size: clamp(1.1rem, 3vw, 1.4rem);
  font-weight: 400;
  color: #5A5A5A;
  margin: 1.5rem 0 2rem;
  max-width: 600px;
  line-height: 1.6;
  opacity: 0.9;
  
  @media (min-width: 768px) {
    margin: 2rem 0 3rem;
  }
`;

const CTAButton = styled(motion.button)`
  font-size: 1rem;
  font-weight: 600;
  padding: 1rem 2rem;
  background: #64A6BD;
  color: #FFFFFF;
  border: none;
  border-radius: 12px;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  box-shadow: 0 8px 20px rgba(100, 166, 189, 0.3);
  transition: all 0.3s ease;
  
  @media (min-width: 768px) {
    padding: 1.2rem 2.5rem;
  }
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 12px 28px rgba(100, 166, 189, 0.4);
    background: #5694A9;
  }
`;

const BannerImage = styled(motion.img)`
  max-width: 100%;
  width: 500px;
  height: auto;
  margin: 0.5rem auto 0;
  display: block;
  filter: drop-shadow(0 5px 15px rgba(0, 0, 0, 0.08));
  
  @media (min-width: 768px) {
    max-width: 700px;
    margin: 0.5rem auto 0;
  }
`;

const FeaturesSection = styled.section`
  padding: 1rem 1.5rem 3rem;
  max-width: 1200px;
  margin: 0 auto;
  position: relative;
  z-index: 10;
  
  @media (min-width: 768px) {
    padding: 1.5rem 4rem 4rem;
  }
`;

const SectionTitle = styled(motion.h2)`
  font-size: clamp(2rem, 5vw, 2.5rem);
  font-weight: 700;
  text-align: center;
  margin-bottom: 0.5rem;
  letter-spacing: -0.02em;
  color: #2D2D2D;
`;

const SectionSubtitle = styled(motion.p)`
  text-align: center;
  color: #5A5A5A;
  font-size: clamp(1rem, 2vw, 1.1rem);
  margin-bottom: 2.5rem;
  opacity: 0.8;
  padding: 0 1rem;
  
  @media (min-width: 768px) {
    margin-bottom: 3rem;
    padding: 0;
  }
`;

const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;
  
  @media (min-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 2rem;
  }
`;

const FeatureCard = styled(motion.div)`
  background: #FFFFFF;
  border-radius: 20px;
  padding: 2rem;
  position: relative;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
  transition: all 0.3s ease;
  
  @media (min-width: 768px) {
    padding: 2.5rem;
  }
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.1);
  }
`;

const FeatureIcon = styled(motion.div)`
  width: 50px;
  height: 50px;
  border-radius: 16px;
  background: linear-gradient(135deg, #D7B9D5 0%, #F4CAE0 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  margin-bottom: 1.5rem;
  box-shadow: 0 4px 15px rgba(215, 185, 213, 0.3);
  
  @media (min-width: 768px) {
    width: 60px;
    height: 60px;
    font-size: 1.8rem;
    margin-bottom: 2rem;
  }
`;

const FeatureTitle = styled.h3`
  font-size: 1.3rem;
  font-weight: 600;
  margin-bottom: 0.8rem;
  color: #2D2D2D;
  
  @media (min-width: 768px) {
    font-size: 1.5rem;
    margin-bottom: 1rem;
  }
`;

const FeatureDescription = styled.p`
  color: #5A5A5A;
  line-height: 1.6;
  font-size: 0.9rem;
  
  @media (min-width: 768px) {
    font-size: 0.95rem;
    line-height: 1.7;
  }
`;

const PlatformSection = styled.section`
  padding: 3rem 1.5rem;
  position: relative;
  z-index: 10;
  background: #F5F5F7;
  
  @media (min-width: 768px) {
    padding: 4rem 4rem;
  }
`;

const PlatformContainer = styled.div`
  max-width: 1000px;
  margin: 0 auto;
  text-align: center;
`;

const PlatformGrid = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2rem;
  margin-top: 2rem;
  
  @media (min-width: 768px) {
    flex-direction: row;
    justify-content: center;
    gap: 3rem;
    margin-top: 3rem;
  }
`;

const Platform = styled(motion.div)`
  width: 150px;
  height: 150px;
  border-radius: 24px;
  background: #FFFFFF;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
  cursor: pointer;
  transition: all 0.3s ease;
  
  @media (min-width: 768px) {
    width: 180px;
    height: 180px;
    gap: 1rem;
  }
  
  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.1);
  }
`;

const PlatformIcon = styled(motion.div)`
  font-size: 3rem;
  color: #64A6BD;
  
  @media (min-width: 768px) {
    font-size: 3.5rem;
  }
`;

const PlatformName = styled.div`
  font-size: 1.1rem;
  font-weight: 600;
  color: #2D2D2D;
  
  @media (min-width: 768px) {
    font-size: 1.2rem;
  }
`;

const Footer = styled.footer`
  padding: 2rem 1.5rem;
  text-align: center;
  border-top: 1px solid #E5E5E5;
  position: relative;
  z-index: 10;
  background: #FFFFFF;
  
  @media (min-width: 768px) {
    padding: 2rem 4rem;
  }
  
  p {
    color: #5A5A5A;
    font-size: 0.8rem;
    letter-spacing: 0.02em;
    margin: 0;
    
    @media (min-width: 768px) {
      font-size: 0.85rem;
    }
    
    span {
      color: #64A6BD;
      font-weight: 600;
    }
  }
`;

const SocialLinks = styled.div`
  display: flex;
  justify-content: center;
  gap: 2rem;
  margin-bottom: 1rem;
  
  a {
    color: #5A5A5A;
    text-decoration: none;
    font-size: 1.5rem;
    transition: all 0.3s ease;
    
    &:hover {
      color: #64A6BD;
      transform: translateY(-2px);
    }
  }
`;

function App() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { scrollY } = useScroll();
  const logoRotate = useTransform(scrollY, [0, 1000], [0, 360]);
  const heroY = useTransform(scrollY, [0, 500], [0, -50]);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
  }, [mobileMenuOpen]);

  const features = [
    {
      icon: "📱",
      title: "QR Code Auth",
      description: "Authenticate TikTok sessions via QR code scan — no passwords stored, no OAuth tokens. Session credentials are encrypted and sealed inside the enclave."
    },
    {
      icon: "📊",
      title: "Watch History Collection",
      description: "Retrieve full watch history data from authenticated TikTok sessions. Structured JSON output ready for analytics pipelines."
    },
    {
      icon: "📡",
      title: "Timeline & Feed Sampling",
      description: "Sample the For You feed and Following timeline programmatically. Capture post metadata, engagement counts, and content signals in real time."
    },
    {
      icon: "🛡️",
      title: "Hardware-Isolated Encryption",
      description: "All credentials and browser sessions run inside a Phala TEE enclave. Three separate encryption keys, whitelisted-only operations, and remote attestation ensure zero-trust security."
    }
  ];

  return (
    <Container>
      <AnimatedBackground>
        {/* Top left - medium orb with gentle diagonal movement */}
        <FloatingOrb
          size={200}
          color="#64A6BD"
          opacity={0.35}
          style={{ top: '10%', left: '10%' }}
          animate={{
            x: [0, 150, -100, 0],
            y: [0, -100, 150, 0],
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            ease: "easeInOut",
            repeatType: "loop"
          }}
        />
        
        {/* Right side - larger orb with vertical emphasis */}
        <FloatingOrb
          size={250}
          color="#90A8C3"
          style={{ top: '50%', right: '5%', opacity: 0.4 }}
          animate={{
            x: [0, -150, 100, 0],
            y: [0, 150, -100, 0],
          }}
          transition={{
            duration: 25,
            repeat: Infinity,
            ease: "easeInOut",
            repeatType: "loop"
          }}
        />
        
        {/* Bottom left - medium orb balancing composition */}
        <FloatingOrb
          size={180}
          color="#ADA7C9"
          style={{ bottom: '20%', left: '30%', opacity: 0.45 }}
          animate={{
            x: [0, 100, -150, 0],
            y: [0, -150, 100, 0],
          }}
          transition={{
            duration: 30,
            repeat: Infinity,
            ease: "easeInOut",
            repeatType: "loop"
          }}
        />
        
        {/* Center-left - small accent orb */}
        <FloatingOrb
          size={120}
          color="#D7B9D5"
          style={{ top: '30%', left: '60%', opacity: 0.3 }}
          animate={{
            x: [0, -100, 100, 0],
            y: [0, 100, -100, 0],
          }}
          transition={{
            duration: 18,
            repeat: Infinity,
            ease: "easeInOut",
            repeatType: "loop"
          }}
        />
        
        {/* Top right corner - medium orb with horizontal movement */}
        <FloatingOrb
          size={160}
          color="#F4CAE0"
          style={{ top: '5%', right: '15%', opacity: 0.35 }}
          animate={{
            x: [0, -200, 50, 0],
            y: [0, 50, -30, 0],
          }}
          transition={{
            duration: 22,
            repeat: Infinity,
            ease: "easeInOut",
            repeatType: "loop"
          }}
        />
        
        {/* Bottom right - tiny accent orb */}
        <FloatingOrb
          size={80}
          color="#64A6BD"
          style={{ bottom: '10%', right: '25%', opacity: 0.25 }}
          animate={{
            x: [0, 80, -80, 0],
            y: [0, -60, 60, 0],
          }}
          transition={{
            duration: 15,
            repeat: Infinity,
            ease: "easeInOut",
            repeatType: "loop"
          }}
        />
        
        {/* Left middle - small orb with circular motion */}
        <FloatingOrb
          size={100}
          color="#90A8C3"
          style={{ top: '60%', left: '5%', opacity: 0.3 }}
          animate={{
            x: [0, 100, 0, -100, 0],
            y: [0, 0, 100, 0, 0],
          }}
          transition={{
            duration: 28,
            repeat: Infinity,
            ease: "easeInOut",
            repeatType: "loop"
          }}
        />
        
        {/* Top center - tiny floating accent */}
        <FloatingOrb
          size={60}
          color="#D7B9D5"
          style={{ top: '15%', left: '45%', opacity: 0.2 }}
          animate={{
            x: [0, 30, -30, 0],
            y: [0, -40, 40, 0],
          }}
          transition={{
            duration: 12,
            repeat: Infinity,
            ease: "easeInOut",
            repeatType: "loop"
          }}
        />
      </AnimatedBackground>
      <PatternBackground />
      
      <NavBar
        scrolled={scrolled}
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <LogoWrapper
          whileHover={{ scale: 1.1 }}
          style={{ rotate: logoRotate }}
          transition={{ type: "spring", stiffness: 400 }}
        >
          <Logo />
        </LogoWrapper>
        <NavLinks>
          <a href="#features">Features</a>
          <a href="#platforms">Platform</a>
        </NavLinks>
        <MobileMenuButton
          open={mobileMenuOpen}
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
        >
          <span />
          <span />
          <span />
        </MobileMenuButton>
      </NavBar>
      
      {mobileMenuOpen && (
        <MobileMenu
          initial={{ x: '100%' }}
          animate={{ x: 0 }}
          exit={{ x: '100%' }}
          transition={{ type: 'spring', stiffness: 300, damping: 30 }}
        >
          <a href="#features" onClick={() => setMobileMenuOpen(false)}>Features</a>
          <a href="#platforms" onClick={() => setMobileMenuOpen(false)}>Platform</a>
        </MobileMenu>
      )}
      
      <Hero>
        <motion.div style={{ y: heroY }}>
          <Title
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            TikTok Data,
            <br />
            <GradientText>Hardware-Sealed</GradientText>
          </Title>
          <Subtitle
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            privacy-first tiktok intelligence platform. qr auth, watch history, feed sampling — encrypted inside a hardware enclave.
          </Subtitle>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
          >
            <CTAButton
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              Request API Access
            </CTAButton>
          </motion.div>
        </motion.div>
        
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.8 }}
        >
          <BannerImage 
            src="/banner.svg" 
            alt="Xordi Banner" 
          />
        </motion.div>
      </Hero>
      
      <FeaturesSection id="features">
        <SectionTitle
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          Platform Capabilities
        </SectionTitle>
        <SectionSubtitle
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          viewport={{ once: true }}
        >
          Privacy-first data collection from TikTok, sealed in hardware
        </SectionSubtitle>
        <FeaturesGrid>
          {features.map((feature, index) => (
            <FeatureCard
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ 
                duration: 0.6, 
                delay: index * 0.1,
                type: "spring",
                stiffness: 100 
              }}
              viewport={{ once: true }}
              whileHover={{ scale: 1.02 }}
            >
              <FeatureIcon
                whileHover={{ rotate: 360 }}
                transition={{ duration: 0.5 }}
              >
                {feature.icon}
              </FeatureIcon>
              <FeatureTitle>{feature.title}</FeatureTitle>
              <FeatureDescription>{feature.description}</FeatureDescription>
            </FeatureCard>
          ))}
        </FeaturesGrid>
      </FeaturesSection>
      
      <PlatformSection id="platforms">
        <PlatformContainer>
          <SectionTitle
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            Built for TikTok
          </SectionTitle>
          <SectionSubtitle
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            viewport={{ once: true }}
          >
            Read feeds, collect history, like, follow — scoped operations only
          </SectionSubtitle>
          <PlatformGrid>
            <Platform
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, type: "spring" }}
              viewport={{ once: true }}
              whileHover={{ scale: 1.05 }}
            >
              <PlatformIcon
                whileHover={{ scale: 1.2 }}
                transition={{ type: "spring", stiffness: 400 }}
              >
                ♪
              </PlatformIcon>
              <PlatformName>TikTok</PlatformName>
            </Platform>
          </PlatformGrid>
        </PlatformContainer>
      </PlatformSection>
      
      <Footer>
        <SocialLinks>
          <a href="https://x.com/xordiIO" target="_blank" rel="noopener noreferrer" aria-label="X (Twitter)">
            𝕏
          </a>
          <a href="https://tiktok.com/@xordiIO" target="_blank" rel="noopener noreferrer" aria-label="TikTok">
            ♪
          </a>
        </SocialLinks>
        <p>© 2026 <span>Xordi</span>. Privacy-first TikTok data infrastructure.</p>
      </Footer>
    </Container>
  );
}

export default App;